import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Visibility as ViewIcon } from '@mui/icons-material';
import dynamicContentService from '@/services/dynamicContentService';

interface LegalDocument {
  id: string;
  documentType: string;
  title: string;
  slug: string;
  version: string;
  effectiveDate: string;
  isActive: boolean;
  requiresAcceptance: boolean;
  appliesTo: string[];
  languageCode: string;
  acceptanceCount: number;
  content?: string;
  contentFormat?: string;
  createdAt: string;
  updatedAt: string;
}

const LegalDocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState<LegalDocument | null>(null);
  const [formData, setFormData] = useState({
    document_type: 'terms_of_service',
    title: '',
    slug: '',
    content: '',
    content_format: 'html',
    version: '1.0',
    effective_date: new Date().toISOString().split('T')[0],
    requires_acceptance: true,
    applies_to: ['all'],
    language_code: 'en'
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await dynamicContentService.getLegalDocuments();

      if (result.success) {
        setDocuments(result.data);
      } else {
        setError(result.error || 'Failed to fetch legal documents');
      }
    } catch (err: any) {
      console.error('Error fetching legal documents:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (document?: LegalDocument) => {
    if (document) {
      // Fetch full document with content from API
      try {
        const result = await dynamicContentService.getLegalDocument(document.id);

        if (result.success) {
          const fullDocument = result.data;
          setEditingDocument(fullDocument);
          setFormData({
            document_type: fullDocument.documentType,
            title: fullDocument.title,
            slug: fullDocument.slug,
            content: fullDocument.content || '',
            content_format: fullDocument.contentFormat || 'html',
            version: fullDocument.version,
            effective_date: fullDocument.effectiveDate.split('T')[0],
            requires_acceptance: fullDocument.requiresAcceptance,
            applies_to: fullDocument.appliesTo,
            language_code: fullDocument.languageCode
          });
        } else {
          setError(result.error || 'Failed to fetch document');
          return;
        }
      } catch (err: any) {
        console.error('Error fetching document:', err);
        setError(err.response?.data?.error || 'Failed to fetch document');
        return;
      }
    } else {
      setEditingDocument(null);
      setFormData({
        document_type: 'terms_of_service',
        title: '',
        slug: '',
        content: '',
        content_format: 'html',
        version: '1.0',
        effective_date: new Date().toISOString().split('T')[0],
        requires_acceptance: true,
        applies_to: ['all'],
        language_code: 'en'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDocument(null);
  };

  const handleSave = async () => {
    setError(null);

    try {
      const result = editingDocument
        ? await dynamicContentService.updateLegalDocument(editingDocument.id, formData)
        : await dynamicContentService.createLegalDocument(formData);

      if (result.success) {
        setSuccess(editingDocument ? 'Document updated successfully' : 'Document created successfully');
        setTimeout(() => setSuccess(null), 3000);
        handleCloseDialog();
        fetchDocuments();
      } else {
        setError(result.error || 'Failed to save document');
      }
    } catch (err: any) {
      console.error('Error saving document:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    }
  };

  const handleView = async (documentId: string) => {
    try {
      const result = await dynamicContentService.getLegalDocument(documentId);

      if (result.success) {
        setEditingDocument(result.data);
        setViewDialog(true);
      }
    } catch (err: any) {
      console.error('Error fetching document:', err);
      setError(err.response?.data?.error || 'Failed to fetch document');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Legal Documents
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Document
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acceptances</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No legal documents found
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Chip
                      label={doc.documentType.replace(/_/g, ' ')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{doc.title}</TableCell>
                  <TableCell>{doc.version}</TableCell>
                  <TableCell>
                    {new Date(doc.effectiveDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={doc.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{doc.acceptanceCount}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleView(doc.id)}>
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(doc)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDocument ? 'Edit Document' : 'Add Legal Document'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={formData.document_type}
                label="Document Type"
                onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
              >
                <MenuItem value="terms_of_service">Terms of Service</MenuItem>
                <MenuItem value="privacy_policy">Privacy Policy</MenuItem>
                <MenuItem value="rental_agreement">Rental Agreement</MenuItem>
                <MenuItem value="cancellation_policy">Cancellation Policy</MenuItem>
                <MenuItem value="insurance_policy">Insurance Policy</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />

            <TextField
              fullWidth
              label="Slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              helperText="URL-friendly identifier (e.g., terms-of-service-v2)"
            />

            <TextField
              fullWidth
              label="Version"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
            />

            <TextField
              fullWidth
              label="Effective Date"
              type="date"
              value={formData.effective_date}
              onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Content"
              multiline
              rows={10}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              helperText="HTML or Markdown content"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requires_acceptance}
                  onChange={(e) => setFormData({ ...formData, requires_acceptance: e.target.checked })}
                />
              }
              label="Requires User Acceptance"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingDocument ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingDocument?.title}</DialogTitle>
        <DialogContent>
          {editingDocument && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Version {editingDocument.version} • Effective {new Date(editingDocument.effectiveDate).toLocaleDateString()}
              </Typography>
              <Box
                sx={{ mt: 2 }}
                dangerouslySetInnerHTML={{ __html: editingDocument.content || '' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegalDocumentsPage;
